import React, { Component } from 'react';
import { includes } from 'lodash';
import { defineMessages, injectIntl } from 'react-intl';
import ContactsImporter from '../contacts-importer';
import { formatMessage } from '../../lib/intl';
import '../../css/emails-input.css';

const i18n = defineMessages({
  errorMessage: {
    id: 'emailsinput.errors.singularErrorMessage',
    defaultMessage: `{total, plural, one {# email has an error } other {# emails have errors}}`,
  },
  placeholder: {
    id: "emailsinput.placeholder",
    defaultMessage: "Type or click to import",
  },
  hint: {
    id: 'emailsinput.hint',
    defaultMessage: 'Separate with commas',
  },
});

class EmailsInput extends Component {
  constructor(props) {
    super(props);

    this.formatMessage = formatMessage(this.props.intl);

    this.state = {
      text: '',
      emails: [],
      invalidEmails: [],
      showImportOptions: false,
    }
  }

  emailImporterWasClicked = false;

  onContainerClick = () => {
    this.input.focus();
  }

  onContactsImporterClick = () => {
    this.emailImporterWasClicked = true;
  }

  onCloseImporter = () => {
    this.emailImporterWasClicked = false;
    this.setState({ showImportOptions: false });
  }

  onTextChange = (e) => {
    const text = e.target.value;

    this.setState({
      text,
    });

    if (this.hasDelimiter(text)) {
      this.addEmailsFromString(text);
      e.preventDefault();
    }
  }

  onKeyInput = (e) => {
    this.setState({
      showImportOptions: false,
    });

    if (e.keyCode === 13 || (e.keyCode === 9 && e.target.value)) {
      e.preventDefault();
      e.stopPropagation();
      this.addEmailsFromString(this.state.text);
    }
  }

  onEmailsFieldFocus = () => {
    this.setState({ showImportOptions: true });
  }

  onEmailsFieldBlur = () => {
    if (this.emailImporterWasClicked) {
      this.emailImporterWasClicked = false;
      return;
    }

    this.setState({ showImportOptions: false }, () => {
      if (this.state.text) {
        this.addEmailsFromString(this.state.text);
      }
    });
  }

  onContactsImported = (contacts) => {
    const emails = [...contacts.map(c => c.selectedEmail())].join(',');
    this.addEmailsFromString(emails);
  }

  _onAdd(emails) {
    this.validateEmails();
  }

  _onInvalids = (invalidEmails) => {
    this.props.onInvalid(invalidEmails);
  }

  recalculateScrollAndHeight() {
    const scrollHeight = this.container.scrollHeight;

    this.container.scrollTop = scrollHeight;

    if (this.container.clientHeight >= 70) {
      this.container.classList.add('down');
    } else {
      this.container.classList.remove('down');
    }
  }

  componentDidUpdate() {
    this.recalculateScrollAndHeight();
  }

  hasDelimiter(text) {
    const { delimiters } = this.props;

    for (let i = 0; i < delimiters.length; i++) {
      let delimiter = String.fromCharCode(delimiters[i]);

      if (text.indexOf(delimiter) !== -1 && text.length !== 1) {
        return true;
      }
    }

    return false;
  }

  deleteFromIndex(index) {
    this.setState({
      emails: this.state.emails.filter((_, i) => i !== index),
    }, () => {
      this.validateEmails();
    });
  }

  addEmailsFromString(string) {
    const that = this;
    const delimiters = this.props.delimiters.map(delimiter => String.fromCharCode(delimiter));

    const emails = string.split(new RegExp(`[${delimiters.join('')}]`))
      .filter((email) => {
        if (email !== '') {
          return true;
        }

        return false;
      })
      .map(email => email.trim());

    that.setState({
      emails: [...that.state.emails, ...emails],

    }, () => {
      this.validateEmails();
    });

    this._onAdd(emails);

    const validEmails = emails.filter((email) => {
      if (this.props.isValid(email)) {
        return true;
      }

      return false;
    });

    if (validEmails.length) {
      this.setState({
        showImportOptions: true,
      });
    }
  }

  validateEmails() {
    const invalidEmails = this.state.emails.filter((email) => {
      if (!this.props.isValid(email)) {
        return true;
      }

      return false;
    });

    this.setState({
      invalidEmails,
    });

    this._onInvalids(invalidEmails);

    this.updateValue();
  }

  updateValue() {
    const { emails } = this.state;
    this.setState({
      text: '',
    });

    this.props.onBlur(makeEvent(emails));
    this.props.onChange(makeEvent(emails));
  }

  renderEmails() {
    const { emails } = this.state;

    return emails.map((email, idx) => {
      let classNames = '';

      if (includes(this.state.invalidEmails, email)) {
        classNames = 'error'
      }

      return (
        <div key={idx} className={`emails-input__tag ${classNames}`}>
          {email}
          <button
            className="emails-input__remove-tag"
            onClick={(e) => { this.deleteFromIndex(idx); e.preventDefault(); return false; }}
          ></button>
        </div>
      );
    });
  }

  renderPlaceholder() {
    const { emails, invalidEmails } = this.state;

    if (emails.length || invalidEmails.length) {
      return null;
    }

    return this.formatMessage(i18n.placeholder);
  }

  renderInputField() {
    return (
      <div className="emails-input__input">
        <input
          name="email"
          ref={(e) => this.input = e}
          type="text"
          value={this.state.text}
          placeholder={this.renderPlaceholder()}
          onChange={this.onTextChange}
          onKeyDown={this.onKeyInput}
          onFocus={this.onEmailsFieldFocus}
          onBlur={this.onEmailsFieldBlur}
        />
      </div>
    );
  }

  renderContactsImportOptions() {
    if (!this.state.showImportOptions) return null;

    return (
      <ContactsImporter
        onImport={this.onContactsImported}
        onClose={this.onCloseImporter}
      />
    );
  }

  renderErrors() {
    const total = this.state.invalidEmails.length;

    if (total === 0) {
      return (
        <span className="subtext">
          <sub className="small-print">
            {this.formatMessage(i18n.hint)}
          </sub>
        </span>
      );
    }

    const errorCopy = this.formatMessage(i18n.errorMessage, { total });

    return (
      <span className="subtext">
        <sub className="small-print error">
          {errorCopy}
        </sub>
      </span>
    );
  }

  render() {
    return (
      <div className="emails-input">
        <label>{this.props.label}</label>
        <div
          className="emails-input__container"
          ref={(e) => this.container = e}
          onClick={this.onContainerClick}
        >
          {this.renderEmails()}
          {this.renderInputField()}
          <div onMouseDown={this.onContactsImporterClick}>
            {this.renderContactsImportOptions()}
          </div>
        </div>
        {this.renderErrors()}
      </div>
    );
  }

}

EmailsInput.defaultProps = {
  emails: [],
  delimiters: ['44', '9'],
  placeholder: '',
  isValid: (email) => {
    /*eslint-disable */
    const r = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    /*eslint-enable */
    return r.test(email);
  },
  onFocus: () => { },
  onBlur: () => { },
  onChange: () => { },
  onInvalid: () => { },
}

const makeEvent = value => ({
  stopPropagation() { },
  preventDefault() { },
  target: { value },
});

export default injectIntl(EmailsInput);



// WEBPACK FOOTER //
// ./src/desktop/components/emails-input/index.js